// 업데이트 내역 — 「이번에 뭐가 바뀌었고 얼마나 더 받았나」
//
// [어디서 오나] 서버에 패치 노트가 없다. 대신 `asset_manifest.json`(경로→해시)과
//   `asset_sizes.json`(경로→바이트)이 **파일 단위 진실**을 들고 있다. 지난 스냅샷과
//   비교하면 추가·변경·삭제와 그 바이트가 그대로 나온다 — 사람이 쓴 것보다 정확하다.
//
// [계약] `checkNow()` 는 «달라졌을 때만» 이력을 하나 쌓는다. 여러 번 불러도 안전하다.
//   기록은 이 브라우저 로컬이라 **주소마다 따로**다(저장소 격리와 같은 규칙).
//
// ⚠ 상세 파일 목록은 항목당 `MAX_FILES` 개까지만 남긴다 — 전량을 넣으면 `localStorage`
//   한도(약 5MB)를 몇 판 만에 넘겨 **이력 전체가 조용히 안 써진다.**

const LOG_KEY = 'sg.updateLog.v1';
const SNAP_KEY = 'sg.updateSnap.v1';
const MAX_ENTRIES = 20;
const MAX_FILES = 150;

const read = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };
const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); return true; } catch { return false; } };

export const getLog = () => read(LOG_KEY, []);
export function clearLog() {
  try { localStorage.removeItem(LOG_KEY); localStorage.removeItem(SNAP_KEY); } catch { /* 사생활 모드 */ }
}

const subs = new Set();
export function subscribe(cb) { subs.add(cb); cb(getLog()); return () => subs.delete(cb); }
const emit = () => { const l = getLog(); for (const cb of subs) { try { cb(l); } catch { /* 구독자 사정 */ } } };

const json = async (u) => {
  const r = await fetch(u, { cache: 'no-store' });
  if (!r.ok) throw new Error(`${u} ${r.status}`);
  return r.json();
};

/** 빌드 시각 — Vite 가 `define` 으로 심는다(`vite.config.js`). 없으면 빈 문자열. */
// eslint-disable-next-line no-undef
const buildStamp = () => { try { return typeof __BUILD_TIME__ === 'string' ? __BUILD_TIME__ : ''; } catch { return ''; } };

/**
 * 지금 서버와 견줘 달라진 게 있으면 이력에 한 줄 쌓는다.
 * @returns 새로 쌓은 항목 · 달라진 게 없으면 null
 */
export async function checkNow() {
  let man; let sizes; let terrainRev = '';
  try {
    [man, sizes] = await Promise.all([json('/asset_manifest.json'), json('/asset_sizes.json')]);
  } catch { return null; }
  // 지형 굽기 지문 — 에셋이 그대로여도 지형만 다시 구워지는 판이 있다(그게 제일 무겁다).
  try { terrainRev = (await json('/terrain_baked/seoul.global.lod.s4.json')).rev || ''; } catch { /* 없으면 그만 */ }

  const prev = read(SNAP_KEY, null);
  const snap = { build: buildStamp(), terrainRev, files: man };
  const sizeOf = (p) => sizes[p] || 0;

  if (!prev) {                                  // 처음 본 기기 — 기준점만 세운다
    write(SNAP_KEY, snap);
    const total = Object.keys(man).reduce((a, p) => a + sizeOf(p), 0);
    const first = { t: Date.now(), build: snap.build, terrainRev, first: true,
      added: Object.keys(man).length, changed: 0, removed: 0, bytes: total, files: [] };
    write(LOG_KEY, [first]);
    emit();
    return first;
  }

  const files = [];
  let added = 0, changed = 0, removed = 0, bytes = 0;
  for (const p of Object.keys(man)) {
    const was = prev.files?.[p];
    if (was === man[p]) continue;
    const kind = was === undefined ? 'added' : 'changed';
    if (kind === 'added') added += 1; else changed += 1;
    bytes += sizeOf(p);
    if (files.length < MAX_FILES) files.push({ p, kind, b: sizeOf(p) });
  }
  for (const p of Object.keys(prev.files || {})) {
    if (man[p] !== undefined) continue;
    removed += 1;
    if (files.length < MAX_FILES) files.push({ p, kind: 'removed', b: 0 });
  }
  const terrainChanged = prev.terrainRev && terrainRev && prev.terrainRev !== terrainRev;
  const buildChanged = prev.build !== snap.build;

  if (!added && !changed && !removed && !terrainChanged && !buildChanged) {
    write(SNAP_KEY, snap);                      // 내용은 같아도 스냅샷은 최신으로 둔다
    return null;
  }

  const entry = { t: Date.now(), build: snap.build, terrainRev, terrainChanged,
    added, changed, removed, bytes, files };
  const log = [entry, ...getLog()].slice(0, MAX_ENTRIES);
  write(LOG_KEY, log);
  write(SNAP_KEY, snap);
  emit();
  return entry;
}

/** 마지막으로 본 지형 굽기 지문. 배지가 「받을 게 남았나」를 묻는 데 쓴다 —
 *  이것 때문에 매니페스트를 다시 받지 않으려고 여기서 내준다. 모르면 빈 문자열. */
export const knownTerrainRev = () => read(SNAP_KEY, null)?.terrainRev || '';

if (typeof window !== 'undefined') window.__updateLog = { checkNow, getLog, clearLog, knownTerrainRev };
